import { useTranslation } from 'next-i18next';

import { useCallback, useState } from 'react';

import { useBreakpointValue, useToken } from '@chakra-ui/react';

import { LocationType, LocationTypeEnum } from '@app/enums';
import { applyPropertyTypeExpansion, mapSearchStateToUrlParams, toNumericIds } from '@app/helpers';
import { calculateZoomFromRadius } from '@app/helpers/map-utils';
import {
    AccommodationItemModel,
    LocationDetailsModel,
    SearchCriteria,
    SearchFilters,
} from '@app/models';
import {
    commonUiStateSlice,
    mapStateSlice,
    setFiltersVisibilityAction,
    setIsMapLoadedAction,
    setLocationDetailsAction,
    setSelectedAccommodationAction,
    toggleFiltersVisibilityAction,
    updateSearchCriteriaAction,
    updateSearchFiltersAction,
    useAppDispatch,
    useAppSelector,
} from '@app/state';
import {
    fetchMapAccommodations,
    getGeoDetails,
    getLocationDetails,
} from '@app/state/api-thunks/map-accommodations.thunk';

import { FeatureToggleEnum } from 'libs/core/src/lib/enums/feature-toggle.enum';

/**
 * Hook to access and update map search state
 */
export function useMapSearch() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('common', { keyPrefix: 'map' });
    const searchCriteria = useAppSelector(mapStateSlice.selectors.selectSearchCriteria);
    const filters = useAppSelector(mapStateSlice.selectors.selectSearchFilters);
    const mapAccommodations = useAppSelector(mapStateSlice.selectors.selectMapAccommodations);
    const isMapLoaded = useAppSelector(mapStateSlice.selectors.selectIsMapLoaded);
    const locationDetails = useAppSelector(mapStateSlice.selectors.selectLocationDetails);
    const hasGeoChanged = useAppSelector(mapStateSlice.selectors.selectHasMapGeoChanged);
    const isLoadingAccommodations = useAppSelector(
        mapStateSlice.selectors.selectIsLoadingAccommodations,
    );
    const accommodationsError = useAppSelector(mapStateSlice.selectors.selectAccommodationsError);
    const selectedAccommodation = useAppSelector(
        mapStateSlice.selectors.selectSelectedAccommodation,
    );
    const isFiltersContentVisible = useAppSelector(
        mapStateSlice.selectors.selectIsFiltersContentVisible,
    );
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isMobileRes = useBreakpointValue(
        {
            base: true,
            lg: false,
        },
        {
            fallback: isMobileBrowser ? 'base' : 'lg',
        },
    );
    const [mapSidebarWidth] = useToken('sizes', ['mapSidebarWidth']);
    const USE_MAP_WITHOUT_PRICES = useAppSelector(
        commonUiStateSlice.selectors.selectUIFtActive<boolean>(
            FeatureToggleEnum.USE_MAP_WITHOUT_PRICES,
        ),
    );

    const [isClustering, setIsClustering] = useState(false);

    const updateSearchCriteria = useCallback(
        (criteria: Partial<SearchCriteria>, shouldUpdateUrl = false) => {
            dispatch(
                updateSearchCriteriaAction({
                    criteria,
                    shouldUpdateUrl,
                }),
            );
        },
        [],
    );

    const updateSearchFilters = useCallback(
        (filters: Partial<SearchFilters>, shouldUpdateUrl = false) => {
            dispatch(
                updateSearchFiltersAction({
                    filters,
                    shouldUpdateUrl,
                }),
            );
        },
        [],
    );

    const setSelectedAccommodation = useCallback((accommodation: AccommodationItemModel | null) => {
        dispatch(setSelectedAccommodationAction({ accommodation }));
    }, []);

    const setLocationDetails = useCallback((data: LocationDetailsModel) => {
        dispatch(
            setLocationDetailsAction({
                locationDetails: data,
            }),
        );
    }, []);

    const toggleFiltersVisibility = useCallback(() => {
        setTimeout(() => {
            dispatch(toggleFiltersVisibilityAction());
        }, 0);
    }, []);

    const setFiltersVisibility = useCallback((open: boolean) => {
        dispatch(setFiltersVisibilityAction({ open }));
    }, []);

    const getAsUrlParams = useCallback(() => {
        return mapSearchStateToUrlParams(searchCriteria || {}, filters || {});
    }, [searchCriteria, filters]);

    const setMapPageTitle = useCallback(
        async (geoId?: string, locationType?: LocationType): Promise<void> => {
            let locative;
            if (geoId && locationType && locationType !== LocationTypeEnum.Accommodation) {
                const geoDetails = await getGeoDetails(geoId, locationType);
                locative = geoDetails?.varietyLocative || geoDetails?.name;
            } else {
                locative = t('metadata.defaultGeoLocative');
            }

            document.title = t('metadata.title', {
                value: locative,
            });
        },
        [t],
    );

    const setMapLocationDetails = useCallback(
        async (geoId: string, locationType: LocationType): Promise<void> => {
            const locationDetails = await getLocationDetails(geoId, locationType);

            if (!locationDetails) {
                return;
            }

            const {
                radius,
                location: { lat },
            } = locationDetails.details;

            const innerWidth = isMobileRes
                ? window.innerWidth
                : window.innerWidth - parseInt(mapSidebarWidth, 10);

            const zoom = calculateZoomFromRadius(radius, innerWidth, window.innerHeight, lat);
            setLocationDetails({
                ...locationDetails,
                details: {
                    ...locationDetails.details,
                    zoom,
                },
            });
        },
        [setLocationDetails, isMobileRes, mapSidebarWidth],
    );

    const fetchAccommodations = useCallback(() => {
        if (!locationDetails) {
            return;
        }

        const basePropertyTypeIds = toNumericIds(filters.propertyType);
        const expandedPropertyTypeIds = applyPropertyTypeExpansion(basePropertyTypeIds);
        const amenityIds = toNumericIds(filters.amenities);
        const provisionIds = toNumericIds(filters.provision);

        // The UI works with ratings on a 0-5 scale, while the nocapi backend expects the same logical rating value expressed on a 0-10.
        const mapUiRatingToApi = (value: number): number => value * 2;

        dispatch(
            fetchMapAccommodations({
                checkInDate: searchCriteria.checkInDate || undefined,
                checkOutDate: searchCriteria.checkOutDate || undefined,
                adultsCount: searchCriteria.guests?.adultsCount,
                childrenCount: searchCriteria.guests?.childrenCount,
                childrenAges: searchCriteria.guests?.childrenAges,
                priceFrom: filters.priceFrom ?? undefined,
                priceTo: filters.priceTo ?? undefined,
                locationDetails,
                propertyTypeIds: expandedPropertyTypeIds,
                amenityIds,
                provisionIds,
                reservationFeatures: filters.reservationFeatures,
                rating: filters.rating ? mapUiRatingToApi(filters.rating) : undefined,
                withoutPrices: USE_MAP_WITHOUT_PRICES,
            }),
        );
    }, [searchCriteria, filters, locationDetails, USE_MAP_WITHOUT_PRICES]);

    const setIsMapLoaded = useCallback((isLoaded: boolean) => {
        dispatch(setIsMapLoadedAction({ isMapLoaded: isLoaded }));
    }, []);

    return {
        searchCriteria,
        filters,
        selectedAccommodation,
        isFiltersContentVisible,
        isMobileRes,
        updateSearchCriteria,
        updateSearchFilters,
        setSelectedAccommodation,
        setLocationDetails,
        setIsMapLoaded,
        toggleFiltersVisibility,
        setFiltersVisibility,
        getAsUrlParams,
        mapAccommodations,
        locationDetails,
        isLoadingAccommodations,
        isMapLoaded,
        isClustering,
        setIsClustering,
        hasGeoChanged,
        accommodationsError,
        setMapLocationDetails,
        setMapPageTitle,
        fetchAccommodations,
    };
}
